class LiveAtomicCart extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog?.showModal) return;
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    document.addEventListener('submit', (event) => this.onSubmit(event), options);
    document.addEventListener('click', (event) => this.onClick(event), options);
    this.addEventListener('change', (event) => this.onChange(event), options);
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const controls = [...this.dialog.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled), [tabindex="0"]')].filter((node) => node.getClientRects().length);
      const first = controls[0];
      const last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }, options);
    this.dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('la-cart-open');
      if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
    }, options);
    this.dialog.addEventListener('click', (event) => {
      if (event.target !== this.dialog) return;
      const box = this.dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
    }, options);
  }

  disconnectedCallback() {
    this.events?.abort();
    this.dialog?.close();
    document.documentElement.classList.remove('la-cart-open');
  }

  show(trigger) {
    if (this.dialog.open) return;
    this.trigger = trigger || document.activeElement;
    document.querySelector('live-atomic-header')?.setMenu(false);
    this.dialog.showModal();
    document.documentElement.classList.add('la-cart-open');
  }

  status(message, failed = false) {
    const status = this.querySelector('[data-cart-status]');
    status.textContent = message;
    status.classList.toggle('la-sr-only', message === 'Cart updated.');
    this.querySelector('[data-cart-recovery]').hidden = !failed;
  }

  setBusy(busy) {
    this.busy = busy;
    this.dialog.setAttribute('aria-busy', String(busy));
    if (busy) {
      const forms = [...document.querySelectorAll('.pdp-product-form, .la-buy-card__form')];
      const submitters = forms.flatMap((form) => [...form.elements].filter((control) => control.type === 'submit'));
      const controls = [...this.querySelectorAll('[data-cart-adjust], [data-cart-remove], [data-cart-quantity], [data-cart-checkout]'), ...submitters];
      this.disabledControls = controls.map((control) => [control, control.disabled]);
      controls.forEach((control) => { control.disabled = true; });
    } else {
      this.disabledControls?.forEach(([control, disabled]) => { control.disabled = disabled; });
    }
  }

  async request(url, options = {}) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal, ...options, headers: { Accept: 'application/json', ...options.headers } });
      const data = await response.json();
      if (!response.ok || data.status >= 400) {
        throw new Error(typeof data.description === 'string' ? data.description : 'This cart update could not be completed. Please check the quantity and try again.');
      }
      return data;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  render(html, focus = null) {
    if (!html) throw new Error('Your cart could not be displayed.');
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const content = parsed.querySelector('[data-cart-content]');
    if (!content) throw new Error('Your cart could not be displayed.');
    const current = this.querySelector('[data-cart-content]');
    const scrollTop = current.querySelector('.la-cart-drawer__items')?.scrollTop || 0;
    current.replaceWith(content);
    const items = content.querySelector('.la-cart-drawer__items');
    if (items) items.scrollTop = scrollTop;
    const count = content.dataset.itemCount;
    document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = count; });
    document.querySelectorAll('[data-cart-open]').forEach((node) => { node.setAttribute('aria-label', `Cart, ${count} ${count === '1' ? 'item' : 'items'}`); });
    if (this.dialog.open && focus) {
      const item = [...content.querySelectorAll('[data-cart-item]')].find((node) => node.dataset.key === focus.key);
      (item?.querySelector(focus.selector) || this.querySelector('[data-cart-close]')).focus({ preventScroll: true });
    }
  }

  async refresh(focus) {
    const url = new URL(this.dataset.cartUrl, window.location.origin);
    url.searchParams.set('sections', this.dataset.sectionId);
    // /cart negotiates plain cart JSON when Accept is application/json,
    // even with sections specified. Request the section-rendering response.
    const sections = await this.request(url, { cache: 'no-store', headers: { Accept: 'text/html' } });
    this.render(sections[this.dataset.sectionId], focus);
  }

  async mutate(url, body, trigger, focus) {
    if (this.busy) return;
    this.setBusy(true);
    this.show(trigger);
    this.status('Updating your cart…');
    try {
      const data = await this.request(url, { method: 'POST', body });
      const html = data.sections?.[this.dataset.sectionId];
      if (html) this.render(html, focus);
      else await this.refresh(focus);
      this.status('Cart updated.');
    } catch (error) {
      // A failed response can still follow a successful cart mutation. Read back
      // the cart, but never automatically repeat a POST and risk duplicate items.
      try { await this.refresh(focus); } catch { /* Keep the recovery link visible. */ }
      this.status(error instanceof TypeError || error.name === 'AbortError' || error instanceof SyntaxError ? 'We could not confirm the update. Check your cart before trying again.' : error.message, true);
    } finally {
      this.setBusy(false);
    }
  }

  onSubmit(event) {
    const form = event.target;
    if (!form.matches('.pdp-product-form, .la-buy-card__form')) return;
    event.preventDefault();
    if (this.busy) return;
    const body = new FormData(form);
    body.set('sections', this.dataset.sectionId);
    body.set('sections_url', window.location.pathname);
    this.mutate(this.dataset.addUrl, body, event.submitter);
  }

  async onClick(event) {
    const trigger = event.target.closest('[data-cart-open]');
    if (trigger && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) {
      event.preventDefault();
      this.show(trigger);
      if (this.busy) return;
      this.setBusy(true);
      this.status('Loading your cart…');
      try { await this.refresh(); this.status(''); }
      catch (error) {
        console.warn('Cart drawer refresh failed:', error);
        this.status('Your cart could not be loaded. Please open the cart page to continue.', true);
      }
      finally { this.setBusy(false); }
      return;
    }
    if (!this.contains(event.target)) return;
    if (event.target.closest('[data-cart-close]')) { this.dialog.close(); return; }
    const control = event.target.closest('[data-cart-adjust], [data-cart-remove]');
    if (!control || this.busy) return;
    const item = control.closest('[data-cart-item]');
    const input = item.querySelector('[data-cart-quantity]');
    const direction = Number(control.dataset.cartAdjust);
    const minimum = Number(input.dataset.minimum);
    const next = Number(input.value) + direction * Number(input.step);
    const quantity = control.hasAttribute('data-cart-remove') || (direction < 0 && next < minimum) ? 0 : Math.max(minimum, next);
    if (input.hasAttribute('max') && quantity > Number(input.max)) return;
    const selector = control.hasAttribute('data-cart-remove') ? '[data-cart-remove]' : `[data-cart-adjust="${direction}"]`;
    this.change(item.dataset.key, quantity, { key: item.dataset.key, selector });
  }

  onChange(event) {
    const input = event.target.closest('[data-cart-quantity]');
    if (!input || this.busy) return;
    const quantity = Number(input.value);
    if (input.value === '' || !Number.isInteger(quantity) || !input.checkValidity() || (quantity > 0 && quantity < Number(input.dataset.minimum))) {
      input.reportValidity();
      this.status('Enter a valid quantity, or use Remove.', true);
      return;
    }
    const key = input.closest('[data-cart-item]').dataset.key;
    this.change(key, quantity, { key, selector: '[data-cart-quantity]' });
  }

  change(key, quantity, focus) {
    const body = new FormData();
    body.set('id', key);
    body.set('quantity', quantity);
    body.set('sections', this.dataset.sectionId);
    body.set('sections_url', window.location.pathname);
    this.mutate(this.dataset.changeUrl, body, null, focus);
  }
}

if (!customElements.get('live-atomic-cart')) customElements.define('live-atomic-cart', LiveAtomicCart);
